import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { ModelInfo } from "@/lib/playground";

type ModelComboboxProps = {
  models: ModelInfo[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
};

const MAX_VISIBLE = 60;

const formatPricePerMillion = (price: string | null) => {
  if (!price) return null;
  const perToken = Number.parseFloat(price);
  if (!Number.isFinite(perToken)) return null;
  return `$${(perToken * 1_000_000).toFixed(2)}/M`;
};

export const ModelCombobox = ({
  models,
  value,
  onChange,
  disabled,
}: ModelComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = needle
      ? models.filter(
          (model) =>
            model.id.toLowerCase().includes(needle) ||
            model.name.toLowerCase().includes(needle)
        )
      : models;
    return matches.slice(0, MAX_VISIBLE);
  }, [models, query]);

  const select = (id: string) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative min-w-0 flex-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left",
          "font-mono text-xs text-foreground transition-colors hover:bg-black/[0.04]",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )}
      >
        <span className="truncate">{value || "pick a model"}</span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-20 border border-foreground bg-popover">
          <div className="border-b p-2">
            <Input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search models…"
              className="h-8 bg-transparent"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 font-mono text-xs text-muted-foreground">
                No models match "{query}".
              </li>
            ) : (
              filtered.map((model) => {
                const price = formatPricePerMillion(model.completionPrice);
                return (
                  <li key={model.id}>
                    <button
                      type="button"
                      onClick={() => select(model.id)}
                      className={cn(
                        "block w-full border-l-2 py-1.5 pl-3 pr-2 text-left text-sm transition-all hover:border-primary hover:bg-secondary hover:pl-4",
                        model.id === value
                          ? "border-primary bg-secondary"
                          : "border-transparent"
                      )}
                    >
                      <span className="block truncate text-sm">
                        {model.name}
                      </span>
                      <span className="block truncate font-mono text-[11px] text-subtle">
                        {model.id}
                        {model.contextLength
                          ? ` · ${Math.round(model.contextLength / 1000)}k ctx`
                          : ""}
                        {price ? ` · ${price} out` : ""}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
